import React, { useEffect, useState } from "react";
import { addIssue, getARJDetails } from "../../api/Issues";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Nav from "../master/NavBar";
import Header from "../master/SideBar";
import UserHelpLayout from "./UserHelpLayout";
import usePrivateLink from "../config/usePrivateLink";

export default function Help() {
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState("");
  const hasAccess = usePrivateLink();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const response = await addIssue(formData);

      if (response.data.status) {
        e.target.reset();
        setPreview("");
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getDetails = async () => {
    setLoading(true);

    try {
      const response = await getARJDetails();

      if (response.data.status) {
        setData(response.data.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setPreview(reader.result);
      };

      reader.readAsDataURL(file);
    }
  };

  const removePreview = () => {
    setPreview("");

    const input = document.getElementById("screenshot");

    if (input) {
      input.value = "";
    }
  };

  useEffect(() => {
    getDetails();
  }, []);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid flex-grow-1 container-p-y">
              <div className="row">
                <UserHelpLayout />

                <div className="col-md-12">
                  {/* ================= HERO ================= */}
                  <div className="card mb-4 overflow-hidden">
                    <div className="card-body p-4 p-md-5">
                      <div className="row align-items-center">
                        <div className="col-lg-8">
                          <div className="d-flex align-items-center gap-3 mb-3">
                            <div
                              className="bg-label-primary rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "52px",
                                height: "52px",
                                flexShrink: 0,
                              }}
                            >
                              <i className="ri-customer-service-2-line ri-28px"></i>
                            </div>

                            <div>
                              <span className="text-muted small fw-medium">
                                SUPPORT CENTER
                              </span>

                              <h2 className="mb-0 mt-1">
                                How can we help you?
                              </h2>
                            </div>
                          </div>

                          <p className="text-muted mb-0 fs-6">
                            Have a question, facing an issue, or need
                            assistance? Send us a message and our support team
                            will get back to you.
                          </p>
                        </div>

                        <div className="col-lg-4 d-none d-lg-flex justify-content-end">
                          <div
                            className="bg-label-primary rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: "130px",
                              height: "130px",
                              opacity: 0.9,
                            }}
                          >
                            <i className="ri-question-answer-line ri-60px"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ================= MAIN CONTENT ================= */}
                  <div className="row g-4">
                    {/* ================= MESSAGE FORM ================= */}
                    <div className="col-xl-7">
                      {hasAccess("/issue/Add") && (
                        <div className="card h-100">
                          <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start mb-4">
                              <div>
                                <h4 className="mb-1">
                                  <i className="ri-send-plane-line me-2 text-primary"></i>
                                  Send us a Message
                                </h4>

                                <p className="text-muted mb-0 small">
                                  Describe your issue and we'll help you resolve
                                  it.
                                </p>
                              </div>

                              <span className="badge bg-label-primary">
                                Support
                              </span>
                            </div>

                            <form onSubmit={handleSubmit}>
                              <div className="mb-4">
                                <label
                                  htmlFor="subject"
                                  className="form-label fw-medium"
                                >
                                  Subject
                                </label>

                                <div className="input-group">
                                  <span className="input-group-text">
                                    <i className="ri-question-line"></i>
                                  </span>

                                  <input
                                    id="subject"
                                    type="text"
                                    className="form-control"
                                    name="subject"
                                    placeholder="What do you need help with?"
                                    required
                                  />
                                </div>
                              </div>

                              <div className="mb-4">
                                <label
                                  htmlFor="message"
                                  className="form-label fw-medium"
                                >
                                  Message
                                </label>

                                <div className="input-group">
                                  <span className="input-group-text align-items-start pt-3">
                                    <i className="ri-message-3-line"></i>
                                  </span>

                                  <textarea
                                    id="message"
                                    className="form-control"
                                    name="message"
                                    rows="6"
                                    placeholder="Please describe your issue in detail..."
                                    required
                                    style={{
                                      resize: "vertical",
                                      minHeight: "140px",
                                    }}
                                  ></textarea>
                                </div>

                                <div className="form-text mt-2">
                                  <i className="ri-information-line me-1"></i>
                                  Include relevant details so we can assist you
                                  faster.
                                </div>
                              </div>

                              {/* ================= SCREENSHOT ================= */}
                              <div className="mb-4">
                                <label className="form-label fw-medium">
                                  Screenshot
                                  <span className="text-muted fw-normal ms-1">
                                    (Optional)
                                  </span>
                                </label>

                                {!preview ? (
                                  <label
                                    htmlFor="screenshot"
                                    className="w-100 d-block"
                                    style={{ cursor: "pointer" }}
                                  >
                                    <div
                                      className="border rounded-3 p-4 text-center"
                                      style={{
                                        borderStyle: "dashed",
                                        transition: "all 0.2s ease",
                                      }}
                                    >
                                      <div className="mb-2">
                                        <span className="avatar avatar-md bg-label-primary rounded-circle">
                                          <i className="ri-image-add-line ri-24px"></i>
                                        </span>
                                      </div>

                                      <h6 className="mb-1">Add a screenshot</h6>

                                      <p className="text-muted small mb-3">
                                        Upload an image to help us understand the
                                        issue
                                      </p>

                                      <span className="btn btn-sm btn-label-primary">
                                        <i className="ri-upload-2-line me-1"></i>
                                        Choose Image
                                      </span>
                                    </div>

                                    <input
                                      type="file"
                                      id="screenshot"
                                      name="img"
                                      hidden
                                      accept="image/*"
                                      capture="environment"
                                      onChange={handleOnChange}
                                    />
                                  </label>
                                ) : (
                                  <div className="border rounded-3 p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="bg-label-success rounded-2 p-2">
                                          <i className="ri-image-line"></i>
                                        </div>

                                        <div>
                                          <h6 className="mb-0">
                                            Screenshot attached
                                          </h6>

                                          <small className="text-muted">
                                            Preview
                                          </small>
                                        </div>
                                      </div>

                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-label-danger"
                                        onClick={removePreview}
                                        title="Remove screenshot"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </div>

                                    <div
                                      className="rounded-3 overflow-hidden text-center"
                                      style={{
                                        maxHeight: "240px",
                                        background: "var(--bs-secondary-bg)",
                                      }}
                                    >
                                      <img
                                        src={preview}
                                        alt="Screenshot preview"
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "240px",
                                          objectFit: "contain",
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* ================= SUBMIT ================= */}
                              <div className="d-flex justify-content-end pt-2">
                                <button
                                  type="submit"
                                  className="btn btn-primary"
                                  disabled={loading}
                                >
                                  <i className="ri-send-plane-fill me-2"></i>
                                  Send Message
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ================= CONTACT INFORMATION ================= */}
                    <div className="col-xl-5">
                      <div className="card h-100">
                        <div className="card-body p-4">
                          <div className="mb-4">
                            <div className="d-flex align-items-center gap-3 mb-2">
                              <div className="bg-label-info rounded-3 p-2">
                                <i className="ri-customer-service-2-line ri-24px"></i>
                              </div>

                              <div>
                                <h4 className="mb-0">Contact Information</h4>
                              </div>
                            </div>

                            {data?.business_name && (
                              <p className="text-muted mb-0 mt-3">
                                {data.business_name}
                              </p>
                            )}
                          </div>

                          <div className="d-flex flex-column gap-3">
                            {/* PHONE */}
                            <div className="border rounded-3 p-3">
                              <div className="d-flex gap-3 align-items-start">
                                <div className="bg-label-success rounded-3 p-2 flex-shrink-0">
                                  <i className="ri-phone-fill ri-22px"></i>
                                </div>

                                <div className="flex-grow-1">
                                  <span className="text-muted small">
                                    Phone
                                  </span>

                                  <h6 className="mb-0 mt-1">
                                    {data?.mobiles || "Not available"}
                                  </h6>
                                </div>
                              </div>
                            </div>

                            {/* EMAIL */}
                            <div className="border rounded-3 p-3">
                              <div className="d-flex gap-3 align-items-start">
                                <div className="bg-label-warning rounded-3 p-2 flex-shrink-0">
                                  <i className="ri-mail-open-line ri-22px"></i>
                                </div>

                                <div className="flex-grow-1">
                                  <span className="text-muted small">
                                    Email
                                  </span>

                                  <h6 className="mb-0 mt-1 text-break">
                                    {data?.email || "Not available"}
                                  </h6>
                                </div>
                              </div>
                            </div>

                            {/* OFFICE */}
                            <div className="border rounded-3 p-3">
                              <div className="d-flex gap-3 align-items-start">
                                <div className="bg-label-danger rounded-3 p-2 flex-shrink-0">
                                  <i className="ri-map-pin-line ri-22px"></i>
                                </div>

                                <div className="flex-grow-1">
                                  <span className="text-muted small">
                                    Office
                                  </span>

                                  <p className="mb-0 mt-1">
                                    {data?.address || "Not available"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* ================= SUPPORT NOTE ================= */}
                          <div className="bg-label-primary rounded-3 p-3 mt-4">
                            <div className="d-flex gap-3 align-items-start">
                              <i className="ri-information-line ri-22px"></i>

                              <div>
                                <h6 className="mb-1">
                                  Need urgent assistance?
                                </h6>

                                <p className="small mb-0">
                                  You can use the contact details above to reach
                                  our support team directly.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
